import { X } from 'lucide-react'
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'

/** Width from which the detail pane stays docked on the right; below it opens as a drawer. */
export const DOCKED_DETAIL_QUERY = '(min-width: 1440px)'

export function useMediaQuery(query: string): boolean {
  const get = () => typeof window !== 'undefined' && window.matchMedia(query).matches
  const [matches, setMatches] = useState(get)
  useEffect(() => {
    const mql = window.matchMedia(query)
    const on = () => setMatches(mql.matches)
    on()
    mql.addEventListener('change', on)
    return () => mql.removeEventListener('change', on)
  }, [query])
  return matches
}

/**
 * List + details. ≥ 1440 px: a 380 px detail column is always on the right (empty state
 * when nothing is selected). 1280–1439 px: details open as a drawer over the list;
 * Esc or ✕ closes it. Esc also clears the selection in the docked layout.
 */
export function SplitView({ list, detail, onCloseDetail, emptyDetail = 'Select a row to see its details.' }: {
  list: ReactNode
  detail: ReactNode | null
  onCloseDetail: () => void
  emptyDetail?: ReactNode
}) {
  const docked = useMediaQuery(DOCKED_DETAIL_QUERY)
  const lastListFocus = useRef<HTMLElement | null>(null)

  const close = () => {
    onCloseDetail()
    // the drawer goes away with focus inside it — put focus back on the row
    lastListFocus.current?.focus()
  }

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape' || detail === null || e.defaultPrevented) return
    e.preventDefault()
    close()
  }

  return (
    <div className="relative flex min-h-0 flex-1" onKeyDown={onKeyDown}>
      <div
        className="flex min-w-0 flex-1 flex-col"
        onFocus={(e) => { lastListFocus.current = e.target as HTMLElement }}
      >
        {list}
      </div>
      {docked ? (
        <aside aria-label="Details" className="w-[380px] shrink-0 overflow-y-auto border-l border-line bg-pane">
          {detail ?? <p className="px-4 py-3.5 text-[12.5px] text-text-3">{emptyDetail}</p>}
        </aside>
      ) : (
        detail !== null && (
          <div
            role="dialog"
            aria-modal="false"
            aria-label="Details"
            // focusable, so a click inside keeps focus within the view and Esc still closes it
            tabIndex={-1}
            className="drawer-in absolute inset-y-0 right-0 z-10 w-[380px] max-w-full overflow-y-auto border-l border-line-strong bg-pane"
          >
            <button
              type="button"
              onClick={close}
              aria-label="Close details"
              title="Close (Esc)"
              className="absolute right-2 top-2 z-[1] rounded-md p-1.5 text-text-2 transition-colors hover:bg-surface-raised hover:text-foreground"
            >
              <X aria-hidden className="size-4" />
            </button>
            {detail}
          </div>
        )
      )}
    </div>
  )
}
